import { selectFeature } from '@app/store/actions.js';
import {
  activeMapToolSelector,
  selectingModeSelector,
} from '@app/store/selectors.js';
import { ElevationRangeLine } from '@features/elevationChart/components/ElevationRangeLine.js';
import { useMap } from '@features/map/hooks/useMap.js';
import {
  colorizeGeometrySource,
  colorizerHotlineOptions,
  NO_DATA_COLOR,
  NO_DATA_OPACITY,
  noDataRuns,
  splitOnGaps,
} from '@shared/colorizers/colorize.js';
import { colorizers } from '@shared/colorizers/index.js';
import { useZoomColorize } from '@shared/colorizers/useZoomColorize.js';
import { RichMarker } from '@shared/components/RichMarker.js';
import { formatDistance } from '@shared/distanceFormatter.js';
import { HALO_COLOR, HALO_WIDTH, SELECTION_COLOR } from '@shared/halo.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { transportTypeDefs } from '@shared/transportTypeDefs.js';
import { along } from '@turf/along';
import { lineString } from '@turf/helpers';
import { length } from '@turf/length';
import type { Feature, LineString, Point } from 'geojson';
import type {
  DragEndEvent,
  LatLng,
  LeafletMouseEvent,
  Polyline as LPolyline,
} from 'leaflet';
import {
  Fragment,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FaPlay, FaStop } from 'react-icons/fa';
import {
  CircleMarker,
  GeoJSON,
  Pane,
  Polyline,
  Tooltip,
  useMapEvent,
} from 'react-leaflet';
import { Hotline } from 'react-leaflet-hotline';
import { useDispatch } from 'react-redux';
import { useRouteColorizeMode } from '../hooks/useRouteColorizeMode.js';
import {
  routePlannerAddPoint,
  routePlannerSetActiveAlternativeIndex,
  routePlannerSetFinish,
  routePlannerSetPoint,
  routePlannerSetStart,
  type StepCoordinate,
} from '../model/actions.js';
import { ISOCHRONE_FILL_OPACITY, isochroneColor } from '../model/isochrones.js';
import { routeColorizeFeatures, tolledMeters } from '../model/pathDetails.js';
import {
  INACTIVE_ALTERNATIVE_COLOR,
  STEP_MODE_COLORS,
  stepModeDashArray,
} from '../model/routeColors.js';
import { useRoutePlannerMessages } from '../translations/useRoutePlannerMessages.js';
import classes from './RoutePlannerResult.module.css';

const pointDraggingClassName = classes.dragging;

// Dedicated pane the route line/outline/colorize render into (declared with the
// `<Pane>` in the render output), so `lineOpacity` can be applied to the whole
// group at once.
const routePaneName = 'fmRoutePlannerRoute';

// Three panes nested in it, so the elevation chart's range band can sit between
// the casing and the line whatever order the layers happen to be added in.
const casingPaneName = 'fmRoutePlannerCasing';
const rangePaneName = 'fmRoutePlannerRange';
const linePaneName = 'fmRoutePlannerLine';

export function RoutePlannerResult(): ReactElement {
  const rpm = useRoutePlannerMessages();

  const dispatch = useDispatch();

  const points = useAppSelector((state) => state.routePlanner.points);

  const finishOnly = useAppSelector((state) => state.routePlanner.finishOnly);

  const alternatives = useAppSelector(
    (state) => state.routePlanner.alternatives,
  );

  const waypoints = useAppSelector((state) => state.routePlanner.waypoints);

  const isochrones = useAppSelector((state) => state.routePlanner.isochrones);

  const activeAlternativeIndex = useAppSelector(
    (state) => state.routePlanner.activeAlternativeIndex,
  );

  const transportType = useAppSelector(
    (state) => state.routePlanner.transportType,
  );

  const mode = useAppSelector((state) => state.routePlanner.mode);

  const colorizeBy = useRouteColorizeMode();

  const lineWidth = useAppSelector(
    (state) => state.routePlannerSettings.lineWidth,
  );

  const lineOpacity = useAppSelector(
    (state) => state.routePlannerSettings.lineOpacity,
  );

  const chartingRoute = useAppSelector(
    (state) => state.elevationChart.target?.type === 'route-planner',
  );

  const markerOpacity = useAppSelector(
    (state) => state.routePlannerSettings.markerOpacity,
  );

  const renderGeojson = useAppSelector(
    (state) => state.routePlanner.renderGeojson,
  );

  const timestamp = useAppSelector((state) => state.routePlanner.timestamp);

  const milestonesMode = useAppSelector(
    (state) => state.routePlanner.milestones,
  );

  const language = useAppSelector((state) => state.l10n.language);

  const zoom = useAppSelector((state) => state.map.zoom);

  const draggingRef = useRef(false);
  const pointPointerDownRef = useRef(false);

  const [dragLatLng, setDragLatLng] = useState<LatLng>();

  const dragSegment = useRef<number | undefined>(undefined);

  const pickMode = useAppSelector((state) => state.routePlanner.pickMode);

  const tool = useAppSelector(activeMapToolSelector);

  const selectedPoint = useAppSelector((state) =>
    state.main.selection?.type === 'route-point'
      ? state.main.selection.id
      : undefined,
  );

  const selectedSegment = useAppSelector((state) =>
    state.main.selection?.type === 'route-leg'
      ? state.main.selection.id
      : undefined,
  );

  const routePlannerToolActive =
    tool === 'route-planner' ||
    selectedPoint !== undefined ||
    selectedSegment !== undefined;

  const interactive =
    useAppSelector(selectingModeSelector) || routePlannerToolActive;

  const onlyStart =
    transportTypeDefs[transportType]?.api === 'gh' && mode !== 'route';

  useMapEvent(
    'click',
    useCallback(
      ({ latlng }: LeafletMouseEvent) => {
        if (window.fmEmbedded || draggingRef.current) {
          // nothing
        } else if (tool !== 'route-planner') {
          // nothing
        } else if (pickMode === 'start' || onlyStart) {
          dispatch(
            routePlannerSetStart({
              lat: latlng.lat,
              lon: latlng.lng,
            }),
          );
        } else if (pickMode === 'finish') {
          dispatch(
            routePlannerSetFinish({
              lat: latlng.lat,
              lon: latlng.lng,
            }),
          );
        }
      },
      [pickMode, dispatch, tool, onlyStart],
    ),
  );

  // The whole route's tolled length. Only the summary shows it: a per-waypoint
  // running total would have to clip the spans, and the figure a driver wants is
  // what the journey costs, not what it has cost so far.
  const tolled = useMemo(
    () => tolledMeters(alternatives[activeAlternativeIndex]),
    [alternatives, activeAlternativeIndex],
  );

  const getPointDetails2 = useCallback(
    (
      distanceSum: number,
      durationSum: number,
      distanceDiff?: number,
      durationDiff?: number,
      showToll = false,
    ) => {
      return (
        <div>
          <div>
            {rpm?.distance({
              value: formatDistance(distanceSum, language),
              diff:
                distanceDiff === undefined
                  ? undefined
                  : formatDistance(distanceDiff, language),
            })}
          </div>
          {!Number.isNaN(durationSum) && (
            <div>
              {rpm?.duration({
                h: Math.floor(Math.round(durationSum / 60) / 60),
                m: Math.round(durationSum / 60) % 60,
                diff:
                  durationDiff === undefined || Number.isNaN(durationDiff)
                    ? undefined
                    : {
                        h: Math.floor(Math.round(durationDiff / 60) / 60),
                        m: Math.round(durationDiff / 60) % 60,
                      },
              })}
            </div>
          )}
          {showToll && tolled > 0 && (
            <div>
              {rpm?.tolled({ value: formatDistance(tolled, language) })}
            </div>
          )}
        </div>
      );
    },
    [language, rpm, tolled],
  );

  const milestones = useMemo(() => {
    if (!milestonesMode || !alternatives[activeAlternativeIndex]) {
      return [];
    }

    const line = lineString(
      alternatives[activeAlternativeIndex].legs
        .flatMap((leg) => leg.steps)
        .filter((step) => step.geometry.coordinates.length > 1)
        .flatMap((step) => step.geometry.coordinates),
    );

    const len = length(line);

    const milestones: Feature<Point>[] = [];

    if (milestonesMode === 'abs') {
      const step =
        zoom > 13
          ? 1
          : zoom > 12
            ? 2
            : zoom > 10
              ? 5
              : zoom > 9
                ? 10
                : zoom > 8
                  ? 20
                  : zoom > 7
                    ? 25
                    : 50;

      for (let d = step; d < len; d += step) {
        const milestone = along(line, d);

        milestone.properties = { label: d };

        milestones.push(milestone);
      }
    } else {
      const pxLen = (len * 2 ** zoom) / 1000;

      const q = 50;

      const steps =
        pxLen < q
          ? pctSeq(50)
          : pxLen < q * 2
            ? pctSeq(25)
            : pxLen < q * 5
              ? pctSeq(10)
              : pxLen < q * 10
                ? pctSeq(5)
                : pxLen < q * 25
                  ? pctSeq(2)
                  : pctSeq(1);

      for (const pct of steps) {
        const milestone = along(line, (len / 100) * pct);

        milestone.properties = { label: pct };

        milestones.push(milestone);
      }
    }

    return milestones;
  }, [activeAlternativeIndex, alternatives, zoom, milestonesMode]);

  const getPointDetails = useCallback(
    (i: number, showDiff = true, summary = false) => {
      let distanceSum = 0;

      let durationSum = 0;

      const offset = summary ? 0 : 1;

      const ii =
        mode === 'route' || summary
          ? i
          : (waypoints[i + offset]?.waypoint_index ?? -10) - offset;

      for (let j = 0; j <= ii; j++) {
        const leg = alternatives[activeAlternativeIndex]?.legs[j];

        if (leg) {
          distanceSum += leg.distance;

          durationSum += leg.duration;
        }
      }

      const leg =
        showDiff && ii > 0
          ? alternatives[activeAlternativeIndex]?.legs[ii]
          : undefined;

      return getPointDetails2(
        distanceSum,
        durationSum,
        leg?.distance,
        leg?.duration,
        summary,
      );
    },
    [alternatives, activeAlternativeIndex, getPointDetails2, mode, waypoints],
  );

  const getSummary = useCallback(
    (showDiff?: boolean) => {
      const { distance } =
        alternatives.find((_, alt) => alt === activeAlternativeIndex) ?? {};

      return distance ? (
        <Tooltip direction="top" permanent>
          <div>{getPointDetails(points.length - 2, showDiff, true)}</div>
        </Tooltip>
      ) : null;
    },
    [alternatives, getPointDetails, points.length, activeAlternativeIndex],
  );

  const handlePointClick = useCallback(
    (position: number) => {
      // also prevent default
      if (selectedPoint !== position) {
        dispatch(selectFeature({ type: 'route-point', id: position }));
      }
    },
    [dispatch, selectedPoint],
  );

  const [pointHovering, setPointHovering] = useState<number>(-1);

  const handlePointMouseOver = useCallback((i: number) => {
    if (!draggingRef.current && !pointPointerDownRef.current) {
      setPointHovering(i);
    }
  }, []);

  const handlePointMouseOut = useCallback(() => {
    if (!draggingRef.current && !pointPointerDownRef.current) {
      setPointHovering(-1);
    }
  }, []);

  const map = useMap();

  // Keep the route pane's opacity in sync with `lineOpacity` as it changes.
  // Applying it to the pane (not per-layer) composites the halo and line as one
  // group: an opaque line fully covers the outline beneath it, then the whole
  // group fades together — the white/blue outline no longer bleeds through a
  // semi-transparent line. The pane itself is created by the `<Pane>` below (its
  // `style` seeds the initial value); this only handles later changes.
  useEffect(() => {
    const pane = map?.getPane(routePaneName);

    if (pane) {
      pane.style.opacity = String(lineOpacity);
    }
  }, [map, lineOpacity]);

  const setPointDraggingUi = useCallback(
    (isDragging: boolean) => {
      draggingRef.current = isDragging;

      map?.getContainer().classList.toggle(pointDraggingClassName, isDragging);
    },
    [map],
  );

  const handlePointDragStart = useCallback(() => {
    setPointDraggingUi(true);
  }, [setPointDraggingUi]);

  const clearPointDraggingUiDeferred = useCallback(() => {
    setTimeout(() => {
      setPointDraggingUi(false);
    });
  }, [setPointDraggingUi]);

  const changeAlternative = useCallback(
    (alternative: number) => {
      // Selecting a segment of the already-active alternative must not
      // re-dispatch: the action clears the cached DEM render line, forcing the
      // colorize processor to re-fetch elevations for no reason.
      if (alternative !== activeAlternativeIndex) {
        dispatch(routePlannerSetActiveAlternativeIndex(alternative));
      }
    },
    [dispatch, activeAlternativeIndex],
  );

  const handlePointDragEnd = useCallback(
    (position: number, event: DragEndEvent) => {
      pointPointerDownRef.current = false;

      clearPointDraggingUiDeferred();

      const { lat, lng: lon } = event.target.getLatLng();

      dispatch(
        routePlannerSetPoint({
          position,
          point: {
            lat,
            lon,
            transport: points[position].transport,
          },
        }),
      );
    },
    [dispatch, points, clearPointDraggingUiDeferred],
  );

  const routePlannerToolActiveRef = useRef(routePlannerToolActive);

  useEffect(() => {
    routePlannerToolActiveRef.current = routePlannerToolActive;
  }, [routePlannerToolActive]);

  const mouseUpTsRef = useRef(0);

  useEffect(() => {
    if (!map) {
      return;
    }

    let moved = false;

    // this is to prevent map-click on drag-end
    const mapContainerClickHandler = (ev: PointerEvent): void => {
      if (Date.now() < mouseUpTsRef.current + 100) {
        ev.preventDefault();
        ev.stopImmediatePropagation();
      }
    };

    map
      .getContainer()
      .addEventListener('click', mapContainerClickHandler, true);

    const handleMouseUp = (e: LeafletMouseEvent) => {
      map.dragging.enable();
      pointPointerDownRef.current = false;

      // Fallback for cases where marker dragend is not emitted.
      clearPointDraggingUiDeferred();

      const segment = dragSegment.current;

      dragSegment.current = undefined;

      if (!moved || segment === undefined) {
        return;
      }

      mouseUpTsRef.current = Date.now();

      dispatch(
        routePlannerAddPoint({
          point: {
            lat: e.latlng.lat,
            lon: e.latlng.lng,
          },
          position: segment,
        }),
      );

      setDragLatLng(undefined);

      moved = false;
    };

    map.addEventListener('mouseup', handleMouseUp);

    const handleMouseMove = (e: LeafletMouseEvent) => {
      if (
        dragSegment.current === undefined ||
        !routePlannerToolActiveRef.current
      ) {
        return;
      }

      moved = true;

      setDragLatLng(e.latlng);
    };

    map.addEventListener('mousemove', handleMouseMove);

    return () => {
      map.removeEventListener('mouseup', handleMouseUp);
      map.removeEventListener('mousemove', handleMouseMove);
      map.getContainer().classList.remove(pointDraggingClassName);
      map
        .getContainer()
        .removeEventListener('click', mapContainerClickHandler, true);
    };
  }, [map, dispatch, clearPointDraggingUiDeferred]);

  const pointElements = useMemo(
    () =>
      points.map((point, i) => {
        const isStart = i === 0 && !finishOnly;

        const isEnd = mode !== 'roundtrip' && i === points.length - 1;

        // `faIcon` and `label` are mutually exclusive on RichMarker.
        const content = isStart
          ? { faIcon: <FaPlay color="#409a40" /> }
          : isEnd
            ? { faIcon: <FaStop color="#d9534f" /> }
            : i === 0
              ? {} // start point in finishOnly mode: neither icon nor label
              : { label: mode === 'route' ? i : waypoints[i]?.waypoint_index };

        return (
          <RichMarker
            key={`pt-${i}`}
            position={{ lat: point.lat, lng: point.lon }}
            opacity={markerOpacity}
            interactive={
              window.fmEmbedded || interactive || selectedPoint === i
            }
            draggable={
              (routePlannerToolActive || selectedPoint === i) &&
              !window.fmEmbedded
            }
            {...content}
            color={isStart ? '#409a40' : isEnd ? '#d9534f' : '#3e64d5'}
            // Selection is the ring, so the waypoint keeps the color that says
            // which of the three it is.
            halo={selectedPoint === i ? SELECTION_COLOR : undefined}
            zIndexOffset={isStart || points.length - 1 ? 10 : 1}
            eventHandlers={
              window.fmEmbedded
                ? {}
                : {
                    mousedown() {
                      pointPointerDownRef.current = true;
                    },
                    dragstart: handlePointDragStart,
                    dragend(e) {
                      handlePointDragEnd(i, e);
                    },
                    click() {
                      handlePointClick(i);
                    },
                    mouseover() {
                      handlePointMouseOver(i);
                    },
                    mouseout: handlePointMouseOut,
                  }
            }
          >
            {i === points.length - 1 /* finish */ ? (
              mode === 'roundtrip' ? (
                <Tooltip direction="top">
                  {getPointDetails(points.length - 2)}
                </Tooltip>
              ) : (
                getSummary(i === pointHovering)
              )
            ) : i === 0 && !finishOnly /* start */ ? (
              mode === 'roundtrip' && getSummary(i === pointHovering)
            ) : (
              <Tooltip direction="top" key={points.length}>
                {getPointDetails(i - 1)}
              </Tooltip>
            )}
          </RichMarker>
        );
      }),
    [
      points,
      routePlannerToolActive,
      interactive,
      selectedPoint,
      mode,
      waypoints,
      finishOnly,
      handlePointDragStart,
      handlePointMouseOut,
      getPointDetails,
      handlePointDragEnd,
      handlePointClick,
      handlePointMouseOver,
      getSummary,
      pointHovering,
      markerOpacity,
    ],
  );

  const activeColorizer = colorizeBy ? colorizers[colorizeBy] : null;

  // Colorize only the active alternative: build one continuous line from its
  // coordinates and split it into gap-free runs the Hotline can draw. The line
  // carries its own white outline, so the route's halo is hidden underneath.
  // Elevation-derived modes use the densified DEM render line once it's ready
  // (`renderGeojson`); otherwise the alternative's own coordinates.
  const colorizeFeatures = useMemo<Feature<LineString>[]>(
    () =>
      activeColorizer
        ? routeColorizeFeatures(
            alternatives[activeAlternativeIndex],
            colorizeGeometrySource(activeColorizer, renderGeojson),
          )
        : [],
    [activeColorizer, renderGeojson, alternatives, activeAlternativeIndex],
  );

  const colorizedPositions = useZoomColorize(
    activeColorizer,
    colorizeFeatures,
    zoom,
  );

  const colorizedRuns = useMemo(
    () => colorizedPositions.flatMap(splitOnGaps),
    [colorizedPositions],
  );

  // Stretches the mode can't value (e.g. missing elevation), drawn in a neutral
  // color so the route stays continuous instead of breaking at the gap.
  const noDataRunsList = useMemo(
    () => colorizedPositions.flatMap(noDataRuns),
    [colorizedPositions],
  );

  // Replace the active alternative's plain line with the Hotline only once
  // colorized (or no-data) runs exist; until then (e.g. elevation still being
  // fetched) the normal line stays visible instead of flashing blank.
  const showColorized = colorizedRuns.length > 0 || noDataRunsList.length > 0;

  // Stable reference so react-leaflet-hotline's options-effect doesn't fire on
  // every render.
  const hotlineOptions = useMemo(
    () => ({
      weight: lineWidth,
      // The white/blue selection outline comes from the wider background halo
      // showing through beneath the canvas, so the hotline carries none.
      outlineWidth: 0,
      ...colorizerHotlineOptions(activeColorizer),
      // Render the colorize canvas into the line pane (a patched
      // react-leaflet-hotline forwards this to its L.Canvas), so it composites
      // above the casing and picks up the route pane's `lineOpacity`.
      pane: linePaneName,
    }),
    [activeColorizer, lineWidth],
  );

  const paths = useMemo(
    () =>
      alternatives
        .map((alternative, index) => ({
          ...alternative,
          alt: index,
          index: index === activeAlternativeIndex ? -1000 : index,
        }))
        .sort((a, b) => b.index - a.index)
        .map(({ legs, alt }) => (
          <Fragment
            key={`alt-${timestamp}--${activeAlternativeIndex}-${alt}-${selectedSegment}`}
          >
            {legs
              .flatMap((leg, legIndex) =>
                leg.steps.map((step) => ({ legIndex, ...step })),
              )
              .map((routeSlice, i: number) =>
                routeSlice.geometry.coordinates.length < 2 ? null : (
                  // Background halo: the white/blue outline plus the click/drag
                  // hit area for every alternative. When colorizing, the
                  // Hotline canvas (its own outline disabled) paints the colored
                  // line on top, so the halo shows through as its edges.
                  <Polyline
                    key={`slice-${i}-${interactive}`}
                    pane={casingPaneName}
                    interactive={interactive && !routeSlice.connector}
                    ref={bringToFront}
                    positions={routeSlice.geometry.coordinates.map(reverse)}
                    // Width lives in pathOptions (not a top-level prop) so
                    // react-leaflet restyles the layer when it changes; opacity
                    // is applied to the whole route pane instead.
                    pathOptions={{
                      weight: lineWidth + HALO_WIDTH,
                      color:
                        selectedSegment === routeSlice.legIndex &&
                        alt === activeAlternativeIndex &&
                        !routeSlice.connector
                          ? SELECTION_COLOR
                          : HALO_COLOR,
                    }}
                    bubblingMouseEvents={false}
                    eventHandlers={{
                      click() {
                        changeAlternative(alt);

                        dispatch(
                          selectFeature({
                            type: 'route-leg',
                            id: routeSlice.legIndex,
                          }),
                        );
                      },

                      mousedown() {
                        if (onlyStart || !routePlannerToolActiveRef.current) {
                          return;
                        }

                        map?.dragging.disable();
                        dragSegment.current = routeSlice.legIndex;
                      },
                    }}
                  />
                ),
              )}

            {legs
              .flatMap((leg, legIndex) =>
                leg.steps.map((step) => ({ legIndex, ...step })),
              )
              .map((routeSlice, i: number) =>
                routeSlice.geometry.coordinates.length < 2 ||
                // The colorized Hotline canvas replaces the active
                // alternative's foreground line; the halo above carries its
                // outline. A failed leg keeps its line — the canvas leaves
                // those metres unpainted, and nothing else says routing gave up.
                (showColorized &&
                  alt === activeAlternativeIndex &&
                  routeSlice.mode !== 'error') ? null : (
                  // foreground
                  <Polyline
                    key={`slice-${timestamp}-${alt}-${i}`}
                    pane={linePaneName}
                    ref={bringToFront}
                    positions={routeSlice.geometry.coordinates.map(reverse)}
                    // Width lives in pathOptions (not a top-level prop) so
                    // react-leaflet restyles the layer when it changes; opacity
                    // is applied to the whole route pane instead.
                    pathOptions={{
                      weight: lineWidth,
                      color:
                        alt !== activeAlternativeIndex
                          ? INACTIVE_ALTERNATIVE_COLOR
                          : STEP_MODE_COLORS[routeSlice.mode],
                    }}
                    dashArray={stepModeDashArray(routeSlice.mode)?.join(', ')}
                    interactive={false}
                    bubblingMouseEvents={false}
                  />
                ),
              )}
          </Fragment>
        )),
    [
      onlyStart,
      alternatives,
      activeAlternativeIndex,
      showColorized,
      timestamp,
      interactive,
      selectedSegment,
      changeAlternative,
      map,
      dispatch,
      lineWidth,
    ],
  );

  return (
    <>
      {!window.fmEmbedded && routePlannerToolActive && dragLatLng && (
        <RichMarker
          interactive={false}
          color={SELECTION_COLOR}
          opacity={0.5}
          position={dragLatLng}
        />
      )}

      {pointElements}

      {/*
       * Route line, outline, and colorize canvas share this pane so `lineOpacity`
       * (seeded here, kept live by the effect above) composites them as one
       * group. `<Pane>` portals its children in only once the pane exists, so the
       * nested layers never attach to a missing pane. Overlay z-level, below the
       * markers; front-to-back order comes from the three panes nested in it.
       */}
      <Pane name={routePaneName} style={{ zIndex: 400, opacity: lineOpacity }}>
        {/* Declared before the layers that name them, so the panes exist by the
            time those are added to the map. */}
        <Pane name={casingPaneName} style={{ zIndex: 1 }} />

        <Pane name={rangePaneName} style={{ zIndex: 2 }}>
          {chartingRoute && (
            <ElevationRangeLine weight={lineWidth + HALO_WIDTH} />
          )}
        </Pane>

        <Pane name={linePaneName} style={{ zIndex: 3 }} />

        {paths}

        {noDataRunsList.map((run, i) => (
          <Polyline
            // `selectedSegment` is in the key, and the layer brought to front,
            // because selecting a leg re-adds the foreground slices, which
            // share this pane and would otherwise cover this line.
            key={`nodata-${colorizeBy}-${timestamp}-${activeAlternativeIndex}-${selectedSegment}-${i}`}
            pane={linePaneName}
            ref={bringToFront}
            positions={run.map((p): [number, number] => [p.lat, p.lon])}
            pathOptions={{
              weight: Math.max(1, lineWidth - 2),
              color: NO_DATA_COLOR,
              // The pane carries `lineOpacity`; this is the no-data run's own
              // dimming relative to the rest of the line.
              opacity: NO_DATA_OPACITY,
              lineCap: 'round',
            }}
            interactive={false}
          />
        ))}

        {colorizedRuns.map((run, i) => (
          <Hotline
            // `lineWidth` in the key remounts the canvas on a width change, which
            // its options prop doesn't restyle live.
            key={`colorize-${colorizeBy}-${lineWidth}-${timestamp}-${activeAlternativeIndex}-${i}`}
            data={run}
            getVal={(p) => p.point.color}
            getLat={(p) => p.point.lat}
            getLng={(p) => p.point.lon}
            options={hotlineOptions}
          />
        ))}

        {isochrones?.map((isochrone) => (
          // `lineWidth` in the key remounts the ring on a width change, which
          // react-leaflet's GeoJSON doesn't restyle live.
          <GeoJSON
            key={`iso-${timestamp}-${isochrone.properties?.['bucket']}-${lineWidth}`}
            pane={linePaneName}
            interactive={false}
            style={(f) => {
              const bucket = f?.properties['bucket'];

              const color = isochroneColor(bucket, isochrones.length);

              // Only the outermost ring is filled; the inner ones would stack
              // their fills into an opaque blob.
              return bucket === isochrones.length - 1
                ? {
                    weight: lineWidth,
                    color,
                    fillOpacity: ISOCHRONE_FILL_OPACITY,
                  }
                : { weight: lineWidth, fill: false, color };
            }}
            data={isochrone}
          />
        ))}
      </Pane>

      {milestones.map((milestone, i) => (
        <CircleMarker
          radius={0}
          key={i}
          center={reverse(milestone.geometry.coordinates as [number, number])}
        >
          <Tooltip className="compact" direction="right" permanent>
            <div>{milestone.properties?.['label']}</div>
          </Tooltip>
        </CircleMarker>
      ))}
    </>
  );
}

function reverse(c: StepCoordinate) {
  return [c[1], c[0]] as [number, number];
}

function pctSeq(step: number) {
  const arr: number[] = [];

  for (let n = step; n < 100; n += step) {
    arr.push(n);
  }

  return arr;
}

function bringToFront(ele: LPolyline) {
  ele?.bringToFront();
}
